import { DownloadApp } from '@/components/download-app';
import {
    AppDispatch,
    DisplayedCarInfoInterface,
    isReserveHomeAllowed,
    isReserveHubAllowed,
} from '@/types';
import SylndrButton from '../../../shared/Button/button-styled.component';
import { NEXT_APP_MAINTENANCE_DISABLE_BOOKING } from '@/constants';
import { toggleLoading } from '@/redux/reducers/checkoutReducer';
import { useDispatch } from 'react-redux';
import Typography from '@/components/shared/typography/typography.component';
import { useAnalytics } from '@/integrations';
import { useRouter } from 'next/navigation';
import NotifyMeButton from '../NotifyMe/Button/notify-me-button.component';
import { useTranslation } from 'react-i18next';
import { AvailableCTAs } from './CarCTAs.enum';

interface ButtonProps {
    carInfo: DisplayedCarInfoInterface;
    financingSource?: string;
}

const FinancingButton = (props: ButtonProps) => {
    const { t } = useTranslation();

    return (
        <DownloadApp
            text={t('financing')}
            deepLink={`Financing-flow/FinancingSylndrOnboarding?carID=${props.carInfo.id}`}
            source={
                props.financingSource || 'Main Financing CTA in Car Profile'
            }
            textVariant="body2SemiBold"
            buttonProps={{
                variant: 'swift',
            }}
        />
    );
};

const BookingButton = (props: ButtonProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { trackEvent } = useAnalytics();
    const router = useRouter();
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const seeCarButtonClicked = () => {
        trackEvent('Action: Book Visit', {
            carName: props.carInfo.carName,
            isRefurbished: props.carInfo.isRefurbished,
            newlyAdded:
                props.carInfo.isNewlyAdded &&
                !(
                    props.carInfo.isReserved ||
                    props.carInfo.isPurchaseInProgress
                ),
            status: props.carInfo.isPurchaseInProgress
                ? 'Being Sold'
                : props.carInfo.isReserved
                ? 'Reserved'
                : 'Not Reserved',
        });
        if (
            props.carInfo?.id === undefined ||
            NEXT_APP_MAINTENANCE_DISABLE_BOOKING
        )
            return;
        router.push(`/${lang}/car-details/${props.carInfo?.id}/checkout/see`);

        dispatch(toggleLoading(false));
    };
    return (
        <SylndrButton
            onClick={seeCarButtonClicked}
            disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
            variant={
                !(
                    props.carInfo.bookingType &
                    (isReserveHubAllowed(props.carInfo?.bookingType) ||
                        isReserveHomeAllowed(props.carInfo?.bookingType))
                ) || props.carInfo.isReserved
                    ? 'primary'
                    : 'secondary'
            }
        >
            <Typography variant="body1SemiBold">{t('See Car')}</Typography>
        </SylndrButton>
    );
};

const ReserveButton = (props: ButtonProps) => {
    const { trackEvent } = useAnalytics();
    const dispatch = useDispatch<AppDispatch>();
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const router = useRouter();

    const reserveCarButtonClick = () => {
        trackEvent('Action: Reserve Car', {
            carName: props.carInfo?.carName,
            isRefurbished: props.carInfo?.isRefurbished,
            newlyAdded:
                props.carInfo?.isNewlyAdded &&
                !(
                    props.carInfo.isReserved ||
                    props.carInfo.isPurchaseInProgress
                ),
            status: props.carInfo?.isPurchaseInProgress
                ? 'Being Sold'
                : props.carInfo?.isReserved
                ? 'Reserved'
                : 'Not Reserved',
        });
        if (
            props.carInfo?.id === undefined ||
            NEXT_APP_MAINTENANCE_DISABLE_BOOKING
        )
            return;
        router.push(
            `/${lang}/car-details/${props.carInfo?.id}/checkout/reserve`
        );
        dispatch(toggleLoading(false));
    };
    return (
        <SylndrButton
            onClick={reserveCarButtonClick}
            className="h-full"
            disabled={
                props.carInfo?.isReserved ||
                NEXT_APP_MAINTENANCE_DISABLE_BOOKING
            }
            variant="primary"
        >
            <Typography variant="body1SemiBold">{t('reserve car')}</Typography>
        </SylndrButton>
    );
};

const NotifyButton = (props: ButtonProps) => (
    <NotifyMeButton carId={props.carInfo.id} />
);

//{ ACTION COMPONENT }

export const CarProfileCTAs: Record<AvailableCTAs, React.FC<ButtonProps>> = {
    FINANCING: FinancingButton,
    BOOKING: BookingButton,
    RESERVE: ReserveButton,
    NOTIFY_ME: NotifyButton,
    //{ ACTION KEY: ACTION COMPONENT }
};
